import { apiClient, ApiResponse } from './api-client'

export interface UserProfile {
  id: number
  phoneNumber: string
  name: string
  email?: string
  userRole: string
  isActive: boolean
  businessId?: number
  createdAt: string
  updatedAt: string
}

export interface UpdateProfileDTO {
  name?: string
  email?: string
  phoneNumber?: string
}

export interface ChangePasswordDTO {
  currentPassword: string
  newPassword: string
}

export class UserService {
  /**
   * Get user profile
   */
  async getProfile(): Promise<UserProfile> {
    const response = await apiClient.get<ApiResponse<{ user: UserProfile }>>('/user/profile')
    if (response.data.success && response.data.data?.user) {
      return response.data.data.user
    }
    throw new Error(response.data.error?.message || 'Failed to get profile')
  }

  /**
   * Update user profile
   */
  async updateProfile(data: UpdateProfileDTO): Promise<UserProfile> {
    const response = await apiClient.put<ApiResponse<{ user: UserProfile }>>('/user/profile', data)
    if (response.data.success && response.data.data?.user) {
      return response.data.data.user
    }
    throw new Error(response.data.error?.message || 'Failed to update profile')
  }

  /**
   * Change password
   */
  async changePassword(data: ChangePasswordDTO): Promise<void> {
    const response = await apiClient.post<ApiResponse>('/user/change-password', data)
    if (!response.data.success) {
      throw new Error(response.data.error?.message || 'Password change failed')
    }
  }

  /**
   * Delete user account (soft delete - set isActive to false)
   */
  async deleteAccount(): Promise<void> {
    const response = await apiClient.delete<ApiResponse>('/user/account')
    if (!response.data.success) {
      throw new Error(response.data.error?.message || 'Failed to delete account')
    }
  }

  /**
   * Get all users by businessId (from token)
   */
  async getAllUsers(): Promise<UserProfile[]> {
    const response = await apiClient.get<ApiResponse<{ users: UserProfile[] }>>('/user/management')
    if (response.data.success && response.data.data?.users) {
      return response.data.data.users
    }
    throw new Error(response.data.error?.message || 'Failed to get users')
  }

  /**
   * Create a new user
   */
  async createUser(data: {
    name: string
    phoneNumber: string
    email?: string
    password: string
    userRole: string
  }): Promise<UserProfile> {
    const response = await apiClient.post<ApiResponse<{ user: UserProfile }>>('/user/management', data)
    if (response.data.success && response.data.data?.user) {
      return response.data.data.user
    }
    throw new Error(response.data.error?.message || 'Failed to create user')
  }

  /**
   * Update a user
   */
  async updateUser(userId: number, data: {
    name?: string
    phoneNumber?: string
    email?: string
    password?: string
    userRole?: string
    isActive?: boolean
  }): Promise<UserProfile> {
    const response = await apiClient.put<ApiResponse<{ user: UserProfile }>>(`/user/management/${userId}`, data)
    if (response.data.success && response.data.data?.user) {
      return response.data.data.user
    }
    throw new Error(response.data.error?.message || 'Failed to update user')
  }

  /**
   * Delete a user (hard delete)
   */
  async deleteUser(userId: number): Promise<void> {
    const response = await apiClient.delete<ApiResponse>(`/user/management/${userId}`)
    if (!response.data.success) {
      throw new Error(response.data.error?.message || 'Failed to delete user')
    }
  }
}

export const userService = new UserService()

